import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { GuestGuideView } from "@/components/GuestGuideView";
import { ConciergeProvider } from "@/components/ConciergeChat";
import { themeTokens } from "@/lib/sections";
import { SectionEditor } from "@/pages/dashboard/SectionEditor";
import { computeCompletion } from "@/pages/dashboard/EditorSections";
import { ExtrasEditor } from "@/components/ExtrasEditor";
import { Icon, newSection, newBlock, sortSections } from "@/lib/guideTemplate";
import { SMART_SECTIONS, SECTION_CATEGORIES } from "@/lib/smartSections";
import { shareGuide } from "@/lib/share";
import { GUEST_LANGS, LANG_NATIVE } from "@/lib/languages";
import { ArrowLeft, Loader2, ChevronUp, ChevronDown, Eye, EyeOff, Copy as CopyIcon, Trash2, Plus, GripVertical, Languages, Globe, Check, Download, ExternalLink, Share2, PartyPopper, Sparkles, Search, BookmarkPlus, Lock, Receipt } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

function sanitizeDraft(p) {
  if (!p) return p;
  const c = JSON.parse(JSON.stringify(p));
  if (c.access) { delete c.access.keybox; delete c.access.access_codes; }
  if (c.wifi) { delete c.wifi.password; }
  delete c.guest_access;
  return c;
}

export default function GuideBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [prop, setProp] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [addCat, setAddCat] = useState("all");
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (addOpen) api.get("/section-templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, [addOpen]);

  const saveAsTemplate = async (s) => {
    try {
      await api.post("/section-templates", { title: s.title, icon: s.icon, subtitle: s.subtitle || "", blocks: s.blocks || [] });
      toast.success("Sezione salvata come template");
    } catch { toast.error("Impossibile salvare il template"); }
  };
  const deleteTemplate = async (id) => {
    try { await api.delete(`/section-templates/${id}`); setTemplates((t) => t.filter((x) => x.id !== id)); }
    catch { toast.error("Impossibile eliminare"); }
  };
  const [busy, setBusy] = useState(false);
  const [langMenu, setLangMenu] = useState(false);
  const [success, setSuccess] = useState(null);
  const [pinInput, setPinInput] = useState("");
  const [saveState, setSaveState] = useState("saved");

  const saveGuestPin = async () => {
    if (pinInput.length < 4) { toast.error("Il PIN deve avere almeno 4 cifre"); return; }
    try {
      await api.put(`/properties/${id}/guest-pin`, { pin: pinInput });
      setProp((p) => ({ ...p, guest_access: { ...(p.guest_access || {}), pin_set: true } }));
      setPinInput(""); toast.success("PIN impostato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeGuestPin = async () => {
    try {
      await api.delete(`/properties/${id}/guest-pin`);
      setProp((p) => ({ ...p, guest_access: { ...(p.guest_access || {}), pin_set: false } }));
      toast.success("PIN rimosso");
    } catch { toast.error("Impossibile rimuovere il PIN"); }
  };

  const [taxBusy, setTaxBusy] = useState(false);
  const setTax = (patch) => setProp((p) => ({ ...p, tourist_tax: { ...(p.tourist_tax || {}), ...patch } }));
  const prefillTax = async () => {
    const comune = prop.basic?.city || prop.tourist_tax?.comune || "";
    if (!comune) { toast.error("Aggiungi prima la città della struttura"); return; }
    setTaxBusy(true);
    try {
      const { data } = await api.get("/tourist-tax/lookup", { params: { comune } });
      setTax({ enabled: true, amount: data.amount, per: data.per, currency: data.currency,
        max_nights: data.max_nights, children_free_under: data.children_free_under, notes: data.notes, comune });
      toast.success(data.matched ? `Regole di ${comune} precompilate` : "Comune non in elenco: valori indicativi, verifica sul sito del Comune");
    } catch { toast.error("Impossibile recuperare le regole"); }
    finally { setTaxBusy(false); }
  };

  useEffect(() => {
    api.get(`/properties/${id}`).then(({ data }) => setProp(data.property))
      .catch(() => { toast.error("Struttura non trovata"); navigate("/dashboard/properties"); });
  }, [id, navigate]);

  const propRef = useRef(prop);
  const savedRef = useRef("");
  const savingRef = useRef(false);
  const pendingRef = useRef(false);
  const timerRef = useRef(null);
  const retryRef = useRef(0);
  const recoveryChecked = useRef(false);

  const draftKey = user?.id && id ? `sg_draft_${user.id}_${id}` : null;
  const clearDraft = () => { if (draftKey) { try { localStorage.removeItem(draftKey); } catch (e) { /* noop */ } } };
  const restoreDraft = () => {
    if (!recovery?.data) { setRecovery(null); return; }
    const d = recovery.data;
    setProp((p) => ({ ...p, ...d,
      access: { ...(p.access || {}), ...(d.access || {}) },
      wifi: { ...(p.wifi || {}), ...(d.wifi || {}) },
      guest_access: p.guest_access }));
    setRecovery(null);
    toast.success("Modifiche ripristinate");
  };
  const dismissDraft = () => { clearDraft(); setRecovery(null); };

  useEffect(() => { propRef.current = prop; }, [prop]);
  useEffect(() => { if (prop && !savedRef.current) savedRef.current = JSON.stringify(prop); }, [prop]);

  const syncSaved = (p) => { savedRef.current = JSON.stringify(p); setSaveState("saved"); clearDraft(); };

  const persist = useCallback(async () => {
    if (savingRef.current) { pendingRef.current = true; return; }
    const snapshot = JSON.stringify(propRef.current);
    if (!snapshot || snapshot === savedRef.current) { setSaveState("saved"); return; }
    savingRef.current = true; setSaveState("saving");
    try {
      await api.put(`/properties/${id}`, JSON.parse(snapshot));
      savedRef.current = snapshot; retryRef.current = 0;
    } catch {
      savingRef.current = false; setSaveState("error");
      if (retryRef.current < 4) {
        retryRef.current += 1;
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(persist, Math.min(8000, 2000 * retryRef.current));
      }
      return;
    }
    savingRef.current = false;
    if (pendingRef.current || JSON.stringify(propRef.current) !== savedRef.current) {
      pendingRef.current = false; setSaveState("dirty");
      clearTimeout(timerRef.current); timerRef.current = setTimeout(persist, 600);
    } else {
      setSaveState("saved");
      if (draftKey) { try { localStorage.removeItem(draftKey); } catch (e) { /* noop */ } }
    }
  }, [id, draftKey]);

  // flush = immediate save, awaited (used before publish / translate / leaving)
  const flush = useCallback(async () => {
    clearTimeout(timerRef.current);
    const snapshot = JSON.stringify(propRef.current);
    if (!snapshot || snapshot === savedRef.current) { setSaveState("saved"); return true; }
    savingRef.current = true; setSaveState("saving");
    try {
      await api.put(`/properties/${id}`, JSON.parse(snapshot));
      savedRef.current = snapshot; retryRef.current = 0; savingRef.current = false;
      setSaveState("saved");
      if (draftKey) { try { localStorage.removeItem(draftKey); } catch (e) { /* noop */ } }
      return true;
    } catch { savingRef.current = false; setSaveState("error"); return false; }
  }, [id, draftKey]);

  const manualSave = async () => {
    const ok = await flush();
    if (ok) toast.success("Modifiche salvate.");
    else toast.error("Non è stato possibile salvare le modifiche. Riprova.");
  };

  // Debounced autosave whenever content changes
  useEffect(() => {
    if (!prop || !savedRef.current) return;
    if (JSON.stringify(prop) === savedRef.current) return;
    setSaveState("dirty");
    if (draftKey) { try { localStorage.setItem(draftKey, JSON.stringify({ updated_at: Date.now(), data: sanitizeDraft(prop) })); } catch (e) { /* noop */ } }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { persist(); }, 1000);
    return () => clearTimeout(timerRef.current);
  }, [prop, persist, draftKey]);

  // Warn before closing/refreshing with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (saveState === "saving" || saveState === "dirty" || saveState === "error") {
        e.preventDefault(); e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [saveState]);

  // Local recovery: offer to restore an unsaved draft found after a crash/close
  useEffect(() => {
    if (!prop || !draftKey || recoveryChecked.current) return;
    recoveryChecked.current = true;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft?.data && JSON.stringify(sanitizeDraft(prop)) !== JSON.stringify(draft.data)) {
        setRecovery(draft);
      } else {
        localStorage.removeItem(draftKey);
      }
    } catch (e) { /* noop */ }
  }, [prop, draftKey]);

  // Retry pending save when the browser comes back online
  useEffect(() => {
    const onOnline = () => { if (savedRef.current && JSON.stringify(propRef.current) !== savedRef.current) persist(); };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [persist]);

  // Best-effort final save on unmount (covers in-app navigation / logout)
  useEffect(() => {
    return () => {
      if (propRef.current && savedRef.current && JSON.stringify(propRef.current) !== savedRef.current) {
        api.put(`/properties/${id}`, propRef.current).catch(() => {});
      }
    };
  }, [id]);

  const sections = sortSections(prop?.sections);
  const move = (i, d) => {
    const s = [...sections]; const j = i + d; if (j < 0 || j >= s.length) return;
    [s[i], s[j]] = [s[j], s[i]];
    setProp((p) => ({ ...p, sections: s.map((x, k) => ({ ...x, position: k })) }));
  };
  const toggle = (sid) => setProp((p) => ({ ...p, sections: p.sections.map((s) => s.id === sid ? { ...s, is_visible: !(s.is_visible ?? true) } : s) }));
  const duplicate = (s) => setProp((p) => ({ ...p, sections: [...p.sections, { ...JSON.parse(JSON.stringify(s)), ...newSection({ key: "custom", title: s.title + " (copia)", subtitle: s.subtitle, icon: s.icon }), type: "custom", blocks: JSON.parse(JSON.stringify(s.blocks || [])), position: p.sections.length }] }));
  const remove = (s) => { if (!window.confirm(`Eliminare la sezione "${s.title}"?`)) return; setProp((p) => ({ ...p, sections: p.sections.filter((x) => x.id !== s.id) })); };
  const addSection = (tpl) => {
    const ns = newSection(tpl);
    if (tpl.blocks?.length) ns.blocks = tpl.blocks.map((b) => { const nb = newBlock(b.type); return { ...nb, content: { ...nb.content, ...(b.content || {}) } }; });
    ns.position = prop.sections.length;
    setProp((p) => ({ ...p, sections: [...p.sections, ns] }));
    setAddOpen(false); setEditing(ns.id);
  };

  const publish = async () => {
    setBusy(true);
    try {
      await flush();
      const { data } = await api.post(`/properties/${id}/publish`);
      setProp(data.property);
      syncSaved(data.property);
      const qr = await api.get(`/properties/${id}/qr`, { params: { base_url: window.location.origin } });
      setSuccess(qr.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const translate = async (target) => {
    setBusy(true); setLangMenu(false);
    try {
      await flush();
      const { data } = await api.post(`/properties/${id}/translate`, null, { params: { target } });
      setProp((p) => ({ ...p, translations: data.translations, languages: data.languages }));
      toast.success(`Traduzione ${LANG_NATIVE[target] || target} generata — controllala prima di pubblicare`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!prop) return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;
  const { percent, missing } = computeCompletion(prop);
  const published = prop.status === "published";
  const concierge = prop.ai_concierge || { enabled: true, tone: "accogliente" };
  const setConcierge = (patch) => {
    const next = { enabled: true, tone: "accogliente", ...(prop.ai_concierge || {}), ...patch };
    setProp((p) => ({ ...p, ai_concierge: next }));
  };
  const T = themeTokens(prop.branding?.theme, prop.branding?.brand_color);
  const existingKeys = new Set((prop.sections || []).map((s) => s.key));
  const addQuery = addSearch.trim().toLowerCase();
  const filteredSmart = SMART_SECTIONS.filter((s) =>
    (addCat === "all" || s.category === addCat) &&
    (!addQuery || s.title.toLowerCase().includes(addQuery) || (s.keywords || "").includes(addQuery) || (s.subtitle || "").toLowerCase().includes(addQuery)));

  return (
    <div className="min-h-screen bg-slate-50 max-w-md mx-auto relative">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-100 h-14 flex items-center justify-between px-4">
        <button onClick={async () => { await flush(); navigate("/dashboard"); }} className="text-slate-500"><ArrowLeft className="w-5 h-5" /></button>
        <div className="text-center min-w-0"><p className="font-display font-bold text-slate-900 truncate text-[15px]">{prop.name}</p><span className={`text-[11px] font-semibold ${published ? "text-emerald-600" : "text-slate-400"}`}>{published ? "● Pubblicata" : "○ Bozza"}</span></div>
        <div className="flex items-center gap-2.5">
          <span data-testid="save-state" className="text-[11px] font-semibold inline-flex items-center gap-1">
            {saveState === "saving" && <><Loader2 className="w-3 h-3 animate-spin text-slate-400" /><span className="text-slate-400">Salvataggio…</span></>}
            {saveState === "saved" && <><Check className="w-3 h-3 text-emerald-500" /><span className="text-emerald-600">Salvato</span></>}
            {saveState === "dirty" && <><span className="w-1.5 h-1.5 rounded-full bg-amber-400" /><span className="text-amber-500">Non salvato</span></>}
            {saveState === "error" && <span className="text-red-500 inline-flex items-center gap-1">⚠ Non salvato</span>}
          </span>
          <button onClick={manualSave} data-testid="save-guide-btn" className="text-blue-600 font-semibold text-sm">Salva</button>
        </div>
      </header>

      <div className="px-4 py-4 space-y-4 pb-40">
        {/* Completion */}
        <div className="rounded-2xl bg-white border border-slate-100 p-4">
          <div className="flex items-center justify-between"><span className="font-semibold text-slate-800 text-sm">La tua guida è completa al {percent}%</span><span className="text-xs text-slate-400">{missing.length} da aggiungere</span></div>
          <div className="mt-2 h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full transition-all duration-700" style={{ width: `${percent}%`, background: percent === 100 ? "#10b981" : "#3B82F6" }} /></div>
          {missing.length > 0 && <p className="text-xs text-slate-400 mt-2">Manca: {missing.slice(0, 3).join(" · ")}</p>}
        </div>

        {/* Assistente AI */}
        <div className="rounded-2xl bg-white border border-slate-100 p-4" data-testid="concierge-settings">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 grid place-items-center shrink-0"><Sparkles className="w-4 h-4" /></div>
              <div className="min-w-0"><p className="font-semibold text-slate-800 text-sm">Concierge AI</p><p className="text-[11px] text-slate-400 leading-tight">Risponde alle domande dei tuoi ospiti 24/7 usando le info della tua struttura</p></div>
            </div>
            <button onClick={() => setConcierge({ enabled: !concierge.enabled })} data-testid="concierge-toggle"
              className={`w-11 h-6 rounded-full relative transition-colors shrink-0 ${concierge.enabled ? "bg-emerald-500" : "bg-slate-200"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${concierge.enabled ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
          {concierge.enabled && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-slate-500 mb-2">Tono dell'assistente</p>
              <div className="grid grid-cols-3 gap-2">
                {[["accogliente", "Accogliente"], ["professionale", "Professionale"], ["informale", "Informale"]].map(([v, l]) => (
                  <button key={v} onClick={() => setConcierge({ tone: v })} data-testid={`concierge-tone-${v}`}
                    className={`rounded-xl py-2 text-xs font-semibold border transition-colors ${concierge.tone === v ? "bg-blue-500 text-white border-blue-500" : "border-slate-200 text-slate-600"}`}>{l}</button>
                ))}
              </div>
              <button onClick={async () => { await flush(); setPreview(true); }} data-testid="concierge-test"
                className="mt-3 w-full rounded-xl border border-blue-200 bg-blue-50 text-blue-700 font-semibold py-2.5 text-sm inline-flex items-center justify-center gap-2"><Sparkles className="w-4 h-4" /> Prova il Concierge AI</button>
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-white border border-slate-100 p-4" data-testid="guest-pin-card">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 grid place-items-center shrink-0"><Lock className="w-4 h-4" /></div>
            <div className="min-w-0"><p className="font-semibold text-slate-800 text-sm">Informazioni di accesso protette</p><p className="text-[11px] text-slate-400 leading-tight">Codici keybox/porta e password Wi-Fi sono nascosti sulla guida pubblica. Gli ospiti li sbloccano con un PIN.</p></div>
          </div>
          <div className="mt-3">
            {prop.guest_access?.pin_set
              ? <span className="text-xs font-semibold text-emerald-600 inline-flex items-center gap-1" data-testid="host-pin-status"><Check className="w-3.5 h-3.5" />PIN attivo</span>
              : <span className="text-xs font-semibold text-slate-400" data-testid="host-pin-status">Nessun PIN impostato — i dati sensibili non sono visibili agli ospiti</span>}
          </div>
          <div className="mt-2 flex gap-2">
            <input value={pinInput} onChange={(e) => setPinInput(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" maxLength={8}
              placeholder={prop.guest_access?.pin_set ? "Nuovo PIN (4-8 cifre)" : "PIN (4-8 cifre)"} data-testid="host-pin-input"
              className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
            <button onClick={saveGuestPin} data-testid="host-pin-save" className="rounded-xl bg-blue-500 text-white font-semibold px-4 text-sm">Salva PIN</button>
          </div>
          {prop.guest_access?.pin_set && <button onClick={removeGuestPin} data-testid="host-pin-remove" className="mt-2 text-xs text-red-500 font-semibold">Rimuovi PIN</button>}
        </div>

        {recovery && (
          <div className="fixed top-16 left-1/2 -translate-x-1/2 z-[70] w-[calc(100%-2rem)] max-w-md" data-testid="recovery-banner">
            <div className="rounded-2xl bg-amber-500 text-white p-3 shadow-xl flex items-center gap-3">
              <div className="min-w-0 flex-1"><p className="font-semibold text-sm">Modifiche non salvate trovate</p><p className="text-[11px] opacity-90">Vuoi ripristinarle?</p></div>
              <button onClick={restoreDraft} data-testid="recovery-restore" className="rounded-lg bg-white text-amber-700 font-bold text-xs px-3 py-1.5">Ripristina</button>
              <button onClick={dismissDraft} data-testid="recovery-dismiss" className="text-white/90 text-xs font-semibold">Ignora</button>
            </div>
          </div>
        )}

        <div className="rounded-2xl bg-white border border-slate-100 p-4" data-testid="tourist-tax-card">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-teal-50 text-teal-600 grid place-items-center shrink-0"><Receipt className="w-4 h-4" /></div>
              <div className="min-w-0"><p className="font-semibold text-slate-800 text-sm">Tassa di soggiorno</p><p className="text-[11px] text-slate-400 leading-tight">Mostrala in guida e falla conoscere al Concierge AI.</p></div>
            </div>
            <button onClick={() => setTax({ enabled: !prop.tourist_tax?.enabled })} data-testid="tourist-tax-toggle"
              className={`w-11 h-6 rounded-full transition-colors shrink-0 ${prop.tourist_tax?.enabled ? "bg-teal-500" : "bg-slate-200"}`}>
              <span className={`block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${prop.tourist_tax?.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>
          {prop.tourist_tax?.enabled && (
            <div className="mt-3 space-y-3">
              <button onClick={prefillTax} disabled={taxBusy} data-testid="tourist-tax-prefill"
                className="w-full rounded-xl bg-teal-50 text-teal-700 font-semibold text-sm py-2.5 inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {taxBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Precompila dalle regole del comune
              </button>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs text-slate-500">Importo (€)
                  <input type="number" step="0.5" min="0" value={prop.tourist_tax?.amount ?? ""} data-testid="tourist-tax-amount"
                    onChange={(e) => setTax({ amount: e.target.value === "" ? null : Number(e.target.value) })}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-500" />
                </label>
                <label className="text-xs text-slate-500">Modalità
                  <select value={prop.tourist_tax?.per || "person_night"} data-testid="tourist-tax-per"
                    onChange={(e) => setTax({ per: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-500 bg-white">
                    <option value="person_night">a persona/notte</option>
                    <option value="person_stay">a persona/soggiorno</option>
                    <option value="stay">a soggiorno</option>
                  </select>
                </label>
                <label className="text-xs text-slate-500">Max notti
                  <input type="number" min="0" value={prop.tourist_tax?.max_nights ?? ""} data-testid="tourist-tax-max-nights"
                    onChange={(e) => setTax({ max_nights: e.target.value === "" ? null : Number(e.target.value) })}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-500" />
                </label>
                <label className="text-xs text-slate-500">Gratis sotto (anni)
                  <input type="number" min="0" value={prop.tourist_tax?.children_free_under ?? ""} data-testid="tourist-tax-children"
                    onChange={(e) => setTax({ children_free_under: e.target.value === "" ? null : Number(e.target.value) })}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-500" />
                </label>
              </div>
              <textarea value={prop.tourist_tax?.notes || ""} data-testid="tourist-tax-notes" rows={2}
                onChange={(e) => setTax({ notes: e.target.value })} placeholder="Note (es. come e dove si paga)"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-500" />
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-white border border-slate-100 p-4">
          <ExtrasEditor extras={prop.extras} onChange={(extras) => setProp((p) => ({ ...p, extras }))} />
        </div>

        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-lg text-slate-900">La tua guida ospiti</h2>
          <div className="relative">
            <button onClick={() => setLangMenu((m) => !m)} disabled={busy} data-testid="translate-menu-toggle" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 disabled:opacity-50"><Languages className="w-3.5 h-3.5" /> Traduci</button>
            {langMenu && (
              <div className="absolute right-0 mt-1 z-30 bg-white rounded-xl shadow-lg border border-slate-100 py-1 w-52" onMouseLeave={() => setLangMenu(false)} data-testid="translate-menu">
                <p className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">Genera traduzione</p>
                {GUEST_LANGS.filter((l) => l.code !== (prop.base_language || "en")).map((l) => {
                  const done = !!(prop.translations?.[l.code]);
                  return (
                    <button key={l.code} onClick={() => translate(l.code)} data-testid={`translate-${l.code}`} className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                      <span>{l.flag}</span> {l.native} {done && <Check className="w-3.5 h-3.5 text-emerald-500 ml-auto" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-400 -mt-2 flex items-center gap-1"><GripVertical className="w-3.5 h-3.5" /> Usa le frecce per riordinare · tocca per modificare</p>

        <div className="space-y-2">
          {sections.map((s, i) => {
            const vis = s.is_visible ?? true;
            return (
              <div key={s.id} className="flex items-center gap-2 rounded-2xl bg-white border border-slate-100 p-3" data-testid={`section-row-${s.key}`}>
                <div className="flex flex-col text-slate-300">
                  <button onClick={() => move(i, -1)}><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => move(i, 1)}><ChevronDown className="w-4 h-4" /></button>
                </div>
                <button onClick={() => setEditing(s.id)} data-testid={`edit-section-${s.key}`} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                  <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${vis ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-400"}`}><Icon name={s.icon} className="w-5 h-5" /></div>
                  <div className="min-w-0"><p className={`font-semibold truncate ${vis ? "text-slate-800" : "text-slate-400"}`}>{s.title}</p><p className="text-xs text-slate-400 truncate">{s.subtitle}</p></div>
                </button>
                <div className="flex items-center gap-1 text-slate-400">
                  <button onClick={() => toggle(s.id)} data-testid={`toggle-${s.key}`} className={vis ? "text-emerald-500" : ""}>{vis ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                  <button onClick={() => duplicate(s)} title="Duplica sezione"><CopyIcon className="w-4 h-4" /></button>
                  <button onClick={() => saveAsTemplate(s)} data-testid={`save-template-${s.key}`} title="Salva come template"><BookmarkPlus className="w-4 h-4" /></button>
                  <button onClick={() => remove(s)} className="hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
        <button onClick={() => setAddOpen(true)} data-testid="add-section" className="w-full inline-flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-2xl py-3.5 text-sm font-semibold text-slate-500 hover:border-blue-400 hover:text-blue-600"><Plus className="w-4 h-4" /> Aggiungi sezione</button>
      </div>

      {/* Sticky actions */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white/95 backdrop-blur border-t border-slate-100 p-3 flex gap-2" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
        <button onClick={() => setPreview(true)} data-testid="preview-guide" className="flex-1 rounded-xl border border-slate-200 font-semibold text-slate-700 py-3">Anteprima ospite</button>
        <button onClick={publish} disabled={busy} data-testid="publish-guide" className="flex-1 rounded-xl bg-blue-500 text-white font-semibold py-3 inline-flex items-center justify-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}{published ? "Aggiorna" : "Pubblica"}</button>
      </div>

      {editing && <SectionEditor prop={prop} setProp={setProp} sectionId={editing} onClose={() => setEditing(null)} />}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center" onClick={() => setPreview(false)}>
          <div className="phone-frame" onClick={(e) => e.stopPropagation()}><div className="phone-screen relative">
            <ConciergeProvider enabled={concierge.enabled} mode="preview" cid={id} brand={T.accent} T={T}>
              <GuestGuideView guide={prop} lang={prop.base_language || "en"} bottomNav />
            </ConciergeProvider>
          </div></div>
          <button onClick={() => setPreview(false)} className="absolute top-5 right-5 text-white text-sm font-semibold">Chiudi</button>
        </div>
      )}

      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto"><SheetHeader><SheetTitle>Aggiungi una sezione</SheetTitle><SheetDescription>Scegli una sezione pronta all'uso o creane una personalizzata.</SheetDescription></SheetHeader>
          <div className="py-4 space-y-4">
            <button onClick={() => addSection({ key: "custom", title: "Nuova sezione", subtitle: "", icon: "info" })} data-testid="add-empty-section" className="w-full flex items-center gap-2 rounded-2xl bg-blue-50 text-blue-700 p-3.5 font-semibold"><Plus className="w-4 h-4" /> Sezione vuota personalizzata</button>

            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={addSearch} onChange={(e) => setAddSearch(e.target.value)} placeholder="Cerca una sezione…" data-testid="section-search" className="w-full rounded-2xl border border-slate-200 pl-9 pr-3 py-2.5 text-sm outline-none focus:border-blue-500" />
            </div>

            <div className="flex gap-2 overflow-x-auto hide-scrollbar -mx-1 px-1">
              {[{ key: "all", label: "Tutte" }, ...SECTION_CATEGORIES, { key: "templates", label: "I tuoi template" }].map((c) => (
                <button key={c.key} onClick={() => setAddCat(c.key)} data-testid={`cat-${c.key}`} className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${addCat === c.key ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>{c.label}</button>
              ))}
            </div>

            {addCat === "templates" ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {templates.map((t) => (
                    <div key={t.id} className="relative text-left rounded-2xl border border-slate-200 bg-white p-3.5" data-testid={`template-${t.id}`}>
                      <button onClick={() => deleteTemplate(t.id)} className="absolute top-2.5 right-2.5 text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => addSection({ key: "custom", title: t.title, subtitle: t.subtitle || "", icon: t.icon, blocks: t.blocks || [] })} data-testid={`use-template-${t.id}`} className="w-full text-left active:scale-[0.98] transition-transform">
                        <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 grid place-items-center"><Icon name={t.icon} className="w-5 h-5" /></div>
                        <p className="mt-2 font-semibold text-slate-800 text-sm leading-tight pr-4">{t.title}</p>
                        <p className="text-[11px] text-slate-400 leading-tight mt-0.5">{(t.blocks || []).length} blocchi</p>
                      </button>
                    </div>
                  ))}
                </div>
                {templates.length === 0 && <p className="text-center text-sm text-slate-400 py-6">Nessun template salvato. Salva una sezione con l'icona segnalibro per riutilizzarla.</p>}
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {filteredSmart.map((tpl) => {
                    const added = existingKeys.has(tpl.key);
                    return (
                      <button key={tpl.key} disabled={added} onClick={() => addSection(tpl)} data-testid={`smart-section-${tpl.key}`}
                        className={`relative text-left rounded-2xl border p-3.5 transition-all ${added ? "border-slate-100 bg-slate-50 opacity-70 cursor-default" : "border-slate-200 bg-white active:scale-[0.98] hover:border-blue-300"}`}>
                        <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 grid place-items-center"><Icon name={tpl.icon} className="w-5 h-5" /></div>
                        <p className="mt-2 font-semibold text-slate-800 text-sm leading-tight">{tpl.title}</p>
                        <p className="text-[11px] text-slate-400 leading-tight mt-0.5">{tpl.subtitle}</p>
                        {added && <span className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full"><Check className="w-2.5 h-2.5" /> Già aggiunta</span>}
                      </button>
                    );
                  })}
                </div>
                {filteredSmart.length === 0 && <p className="text-center text-sm text-slate-400 py-6">Nessuna sezione trovata</p>}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {success && <PublishSuccess data={success} name={prop.name} onClose={() => setSuccess(null)} />}
    </div>
  );
}

function PublishSuccess({ data, name, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(data.url); setCopied(true); toast.success("Link copiato"); setTimeout(() => setCopied(false), 1500); };
  const download = () => { const a = document.createElement("a"); a.href = data.qr; a.download = `${data.slug}-qr.png`; a.click(); };
  return (
    <div className="fixed inset-0 z-[60] bg-white overflow-y-auto" data-testid="publish-success">
      <div className="max-w-md mx-auto px-6 py-10 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-500 grid place-items-center mx-auto animate-fade-up"><PartyPopper className="w-8 h-8" /></div>
        <h1 className="mt-5 font-display font-extrabold text-2xl text-slate-900">La tua guida è online! 🎉</h1>
        <p className="mt-2 text-slate-500">Condividila con i tuoi ospiti tramite link o QR code.</p>
        <div className="mt-6 rounded-3xl bg-white border border-slate-100 p-5 shadow-sm inline-block"><img src={data.qr} alt="QR" className="w-52 h-52" data-testid="success-qr" /></div>
        <p className="mt-3 text-sm font-mono text-slate-500 break-all">{data.url}</p>
        <div className="mt-6 space-y-2">
          <button onClick={() => shareGuide(data.url, name)} className="w-full rounded-xl bg-blue-500 text-white font-semibold py-3 inline-flex items-center justify-center gap-2"><Share2 className="w-4 h-4" /> Condividi con gli ospiti</button>
          <div className="grid grid-cols-3 gap-2">
            <button onClick={copy} className="rounded-xl border border-slate-200 font-semibold text-slate-700 py-3 text-sm inline-flex items-center justify-center gap-1.5">{copied ? <Check className="w-4 h-4 text-emerald-500" /> : <CopyIcon className="w-4 h-4" />} Copia</button>
            <button onClick={download} className="rounded-xl border border-slate-200 font-semibold text-slate-700 py-3 text-sm inline-flex items-center justify-center gap-1.5"><Download className="w-4 h-4" /> QR</button>
            <a href={data.url} target="_blank" rel="noreferrer" className="rounded-xl border border-slate-200 font-semibold text-slate-700 py-3 text-sm inline-flex items-center justify-center gap-1.5"><ExternalLink className="w-4 h-4" /> Apri</a>
          </div>
        </div>
        <button onClick={onClose} className="mt-6 text-sm font-semibold text-slate-500">Torna alla guida</button>
      </div>
    </div>
  );
}
